import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { LogOut, Plus, Pencil, Trash2, Upload, Tag, Image as ImageIcon, CalendarCheck, Settings as SettingsIcon, ExternalLink, Star, MessageSquareQuote } from "lucide-react";
import { toast } from "sonner";
import { api, imgSrc, formatApiError } from "@/lib/api";
import { COLORING_CATEGORIES, GALLERY_FILTERS } from "@/data/static";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";

const TABS = [
  { id: "gallery", label: "Gallery", icon: ImageIcon },
  { id: "reviews", label: "Review", icon: MessageSquareQuote },
  { id: "bookings", label: "Booking Masuk", icon: CalendarCheck },
  { id: "settings", label: "Pengaturan", icon: SettingsIcon },
];

const EMPTY_REVIEW = { name: "", service: "", rating: 5, text: "" };

const EMPTY_PROMO = { name: "", normal_price: "", promo_price: "", valid_until: "", benefits: [], image: "", active: true };
const EMPTY_GALLERY = { type: "coloring", category: "BASIC COLOR", title: "", description: "", image: "", before_image: "", after_image: "" };

const inputCls = "w-full rounded-sm border border-brand-gold/30 bg-white px-4 py-3 text-sm text-brand-ink outline-none transition-colors focus:border-brand-gold focus:ring-1 focus:ring-brand-gold/40";
const labelCls = "mb-1.5 block text-xs font-semibold uppercase tracking-[0.15em] text-brand-burgundy";

const ImageField = ({ label, value, onChange, testid }) => {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd);
      onChange(data.url);
      toast.success("Foto berhasil diupload");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
    }
  };

  return (
    <div>
      <label className={labelCls}>{label}</label>
      <div className="flex gap-2">
        <input data-testid={`${testid}-url-input`} value={value} onChange={(e) => onChange(e.target.value)} placeholder="URL gambar atau upload →" className={inputCls} />
        <button type="button" data-testid={`${testid}-upload-btn`} onClick={() => fileRef.current?.click()} disabled={uploading} className="shrink-0 rounded-sm bg-brand-burgundy px-4 text-brand-cream transition-colors hover:bg-brand-gold hover:text-brand-ink disabled:opacity-60">
          <Upload size={16} />
        </button>
        <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={upload} />
      </div>
      {uploading && <p className="mt-1 text-xs text-brand-ink-muted">Mengupload...</p>}
      {value && <img src={imgSrc(value)} alt="preview" className="mt-2 h-20 w-32 rounded-sm object-cover gold-foil-border" />}
    </div>
  );
};

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("gallery");
  const [promos, setPromos] = useState([]);
  const [gallery, setGallery] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [settings, setSettings] = useState(null);
  const [promoForm, setPromoForm] = useState(null);
  const [galleryForm, setGalleryForm] = useState(null);
  const [reviewForm, setReviewForm] = useState(null);
  const [benefitsText, setBenefitsText] = useState("");
  const [galleryTypeFilter, setGalleryTypeFilter] = useState("coloring");

  const loadAll = useCallback(async () => {
    try {
      await api.get("/auth/me");
    } catch {
      localStorage.removeItem("sbb_token");
      navigate("/admin");
      return;
    }
    const [p, g, b, s, rv] = await Promise.all([
      api.get("/promos", { params: { all: true } }).catch(() => ({ data: [] })),
      api.get("/gallery").catch(() => ({ data: [] })),
      api.get("/bookings").catch(() => ({ data: [] })),
      api.get("/settings").catch(() => ({ data: null })),
      api.get("/reviews").catch(() => ({ data: [] })),
    ]);
    setPromos(p.data);
    setGallery(g.data);
    setBookings(b.data);
    setSettings(s.data);
    setReviews(rv.data);
  }, [navigate]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const logout = () => {
    localStorage.removeItem("sbb_token");
    navigate("/admin");
  };

  const savePromo = async () => {
    const payload = { ...promoForm, benefits: benefitsText.split("\n").map((b) => b.trim()).filter(Boolean) };
    try {
      if (payload.id) await api.put(`/promos/${payload.id}`, payload);
      else await api.post("/promos", payload);
      toast.success("Promo tersimpan");
      setPromoForm(null);
      loadAll();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const deletePromo = async (id) => {
    try {
      await api.delete(`/promos/${id}`);
      toast.success("Promo dihapus");
      loadAll();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const saveGallery = async () => {
    try {
      if (galleryForm.id) await api.put(`/gallery/${galleryForm.id}`, galleryForm);
      else await api.post("/gallery", galleryForm);
      toast.success("Item gallery tersimpan");
      setGalleryForm(null);
      loadAll();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const deleteGallery = async (id) => {
    try {
      await api.delete(`/gallery/${id}`);
      toast.success("Item dihapus");
      loadAll();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const saveSettings = async () => {
    try {
      await api.put("/settings", settings);
      toast.success("Pengaturan tersimpan");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const saveReview = async () => {
    try {
      const payload = { ...reviewForm, rating: Number(reviewForm.rating) || 5 };
      if (payload.id) await api.put(`/reviews/${payload.id}`, payload);
      else await api.post("/reviews", payload);
      toast.success("Review tersimpan");
      setReviewForm(null);
      loadAll();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const deleteReview = async (id) => {
    try {
      await api.delete(`/reviews/${id}`);
      toast.success("Review dihapus");
      loadAll();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const deleteBooking = async (id) => {
    await api.delete(`/bookings/${id}`).catch(() => {});
    loadAll();
  };

  const filteredGallery = gallery.filter((g) => g.type === galleryTypeFilter);
  const categoryOptions = galleryForm?.type === "coloring" ? COLORING_CATEGORIES.slice(1) : GALLERY_FILTERS.slice(1);

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-brand-paper">
      <header className="sticky top-0 z-40 border-b border-brand-gold/25 bg-brand-burgundy">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-5 sm:px-8">
          <p className="font-cinzel text-base font-bold tracking-[0.15em] text-brand-cream">
            SALON <span className="text-brand-gold">STRAWBERRY</span>
            <span className="ml-3 rounded-full bg-brand-gold/20 px-3 py-1 text-[9px] uppercase tracking-[0.25em] text-brand-gold">Admin</span>
          </p>
          <div className="flex items-center gap-3">
            <Link data-testid="admin-view-site-btn" to="/" className="hidden items-center gap-2 text-xs uppercase tracking-[0.15em] text-brand-cream/70 transition-colors hover:text-brand-gold sm:flex">
              <ExternalLink size={14} /> Lihat Website
            </Link>
            <button data-testid="admin-logout-btn" onClick={logout} className="flex items-center gap-2 rounded-full border border-brand-gold/40 px-4 py-2 text-xs uppercase tracking-[0.15em] text-brand-gold transition-colors hover:bg-brand-gold hover:text-brand-ink">
              <LogOut size={13} /> Keluar
            </button>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-7xl px-5 sm:px-8 py-10">
        <div className="mb-8 flex flex-wrap gap-2" data-testid="admin-tabs">
          {TABS.map((t) => (
            <button
              key={t.id}
              data-testid={`admin-tab-${t.id}`}
              onClick={() => setTab(t.id)}
              className={`flex items-center gap-2 rounded-full px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.15em] transition-all duration-300 ${
                tab === t.id ? "bg-brand-burgundy text-brand-cream shadow-lg" : "bg-white text-brand-ink-muted gold-foil-border hover:text-brand-burgundy"
              }`}
            >
              <t.icon size={14} /> {t.label}
            </button>
          ))}
        </div>

        {tab === "promo" && (
          <div data-testid="admin-promo-panel">
            <div className="mb-6 flex items-center justify-between">
              <h2 className="font-serif text-2xl font-semibold text-brand-burgundy">Kelola Promo ({promos.length})</h2>
              <button data-testid="admin-add-promo-btn" onClick={() => { setPromoForm({ ...EMPTY_PROMO }); setBenefitsText(""); }} className="flex items-center gap-2 rounded-full bg-brand-burgundy px-5 py-2.5 text-xs font-bold uppercase tracking-[0.15em] text-brand-cream transition-colors hover:bg-brand-gold hover:text-brand-ink">
                <Plus size={14} /> Tambah Promo
              </button>
            </div>
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {promos.map((p) => (
                <div key={p.id} data-testid={`admin-promo-item-${p.id}`} className="rounded-sm bg-white gold-foil-border p-6">
                  <div className="flex items-start justify-between gap-3">
                    <h3 className="font-serif text-lg font-semibold text-brand-burgundy">{p.name}</h3>
                    <span className={`shrink-0 rounded-full px-3 py-1 text-[9px] font-bold uppercase tracking-[0.15em] ${p.active ? "bg-emerald-100 text-emerald-800" : "bg-stone-200 text-stone-500"}`}>
                      {p.active ? "Aktif" : "Nonaktif"}
                    </span>
                  </div>
                  <p className="mt-2 text-sm text-brand-ink-muted"><s>{p.normal_price}</s> <span className="font-bold text-brand-gold">{p.promo_price}</span></p>
                  <p className="mt-1 text-xs text-brand-ink-muted">{p.valid_until}</p>
                  <div className="mt-4 flex gap-2">
                    <button data-testid={`admin-promo-edit-${p.id}`} onClick={() => { setPromoForm({ ...p }); setBenefitsText((p.benefits || []).join("\n")); }} className="flex items-center gap-1.5 rounded-full border border-brand-gold/40 px-4 py-2 text-xs text-brand-burgundy transition-colors hover:bg-brand-burgundy hover:text-brand-cream">
                      <Pencil size={12} /> Edit
                    </button>
                    <button data-testid={`admin-promo-delete-${p.id}`} onClick={() => deletePromo(p.id)} className="flex items-center gap-1.5 rounded-full border border-red-200 px-4 py-2 text-xs text-red-700 transition-colors hover:bg-red-600 hover:text-white">
                      <Trash2 size={12} /> Hapus
                    </button>
                  </div>
                </div>
              ))}
              {promos.length === 0 && <p className="text-sm text-brand-ink-muted">Belum ada promo. Tambahkan promo pertama Anda.</p>}
            </div>
          </div>
        )}

        {tab === "gallery" && (
          <div data-testid="admin-gallery-panel">
            <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
              <h2 className="font-serif text-2xl font-semibold text-brand-burgundy">Kelola Gallery ({filteredGallery.length})</h2>
              <div className="flex items-center gap-2">
                <div className="flex rounded-full bg-white gold-foil-border p-1">
                  {[["coloring", "Coloring"], ["general", "Umum"]].map(([v, l]) => (
                    <button key={v} data-testid={`admin-gallery-type-${v}`} onClick={() => setGalleryTypeFilter(v)} className={`rounded-full px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.12em] transition-colors ${galleryTypeFilter === v ? "bg-brand-burgundy text-brand-cream" : "text-brand-ink-muted"}`}>
                      {l}
                    </button>
                  ))}
                </div>
                <button data-testid="admin-add-gallery-btn" onClick={() => setGalleryForm({ ...EMPTY_GALLERY, type: galleryTypeFilter, category: galleryTypeFilter === "coloring" ? "BASIC COLOR" : "Hair" })} className="flex items-center gap-2 rounded-full bg-brand-burgundy px-5 py-2.5 text-xs font-bold uppercase tracking-[0.15em] text-brand-cream transition-colors hover:bg-brand-gold hover:text-brand-ink">
                  <Plus size={14} /> Tambah Foto
                </button>
              </div>
            </div>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {filteredGallery.map((g) => (
                <div key={g.id} data-testid={`admin-gallery-item-${g.id}`} className="overflow-hidden rounded-sm bg-white gold-foil-border">
                  {g.image && <img src={imgSrc(g.image)} alt={g.title} className="h-36 w-full object-cover" />}
                  <div className="p-4">
                    <span className="text-[9px] font-bold uppercase tracking-[0.2em] text-brand-gold">{g.category}</span>
                    <h3 className="mt-1 font-serif text-sm font-semibold text-brand-burgundy">{g.title || "(tanpa judul)"}</h3>
                    <div className="mt-3 flex gap-2">
                      <button data-testid={`admin-gallery-edit-${g.id}`} onClick={() => setGalleryForm({ ...g })} className="flex items-center gap-1 rounded-full border border-brand-gold/40 px-3 py-1.5 text-[11px] text-brand-burgundy transition-colors hover:bg-brand-burgundy hover:text-brand-cream">
                        <Pencil size={11} /> Edit
                      </button>
                      <button data-testid={`admin-gallery-delete-${g.id}`} onClick={() => deleteGallery(g.id)} className="flex items-center gap-1 rounded-full border border-red-200 px-3 py-1.5 text-[11px] text-red-700 transition-colors hover:bg-red-600 hover:text-white">
                        <Trash2 size={11} /> Hapus
                      </button>
                    </div>
                  </div>
                </div>
              ))}
              {filteredGallery.length === 0 && <p className="text-sm text-brand-ink-muted">Belum ada foto di kategori ini.</p>}
            </div>
          </div>
        )}

        {tab === "bookings" && (
          <div data-testid="admin-bookings-panel">
            <h2 className="mb-6 font-serif text-2xl font-semibold text-brand-burgundy">Booking Masuk ({bookings.length})</h2>
            <div className="space-y-3">
              {bookings.map((b) => (
                <div key={b.id} data-testid={`admin-booking-item-${b.id}`} className="flex flex-wrap items-center justify-between gap-4 rounded-sm bg-white gold-foil-border p-5">
                  <div>
                    <p className="font-serif text-base font-semibold text-brand-burgundy">{b.name} — {b.service}</p>
                    <p className="mt-1 text-xs text-brand-ink-muted">WA: {b.whatsapp} • Tanggal: {b.date} {b.notes ? `• Catatan: ${b.notes}` : ""}</p>
                  </div>
                  <button data-testid={`admin-booking-delete-${b.id}`} onClick={() => deleteBooking(b.id)} className="flex items-center gap-1.5 rounded-full border border-red-200 px-4 py-2 text-xs text-red-700 transition-colors hover:bg-red-600 hover:text-white">
                    <Trash2 size={12} /> Hapus
                  </button>
                </div>
              ))}
              {bookings.length === 0 && <p className="text-sm text-brand-ink-muted">Belum ada booking masuk dari form website.</p>}
            </div>
          </div>
        )}

        {tab === "reviews" && (
          <div data-testid="admin-reviews-panel">
            <div className="mb-6 flex items-center justify-between">
              <h2 className="font-serif text-2xl font-semibold text-brand-burgundy">Kelola Review ({reviews.length})</h2>
              <button data-testid="admin-add-review-btn" onClick={() => setReviewForm({ ...EMPTY_REVIEW })} className="flex items-center gap-2 rounded-full bg-brand-burgundy px-5 py-2.5 text-xs font-bold uppercase tracking-[0.15em] text-brand-cream transition-colors hover:bg-brand-gold hover:text-brand-ink">
                <Plus size={14} /> Tambah Review
              </button>
            </div>
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {reviews.map((r) => (
                <div key={r.id} data-testid={`admin-review-item-${r.id}`} className="rounded-sm bg-white gold-foil-border p-6">
                  <div className="flex gap-0.5">
                    {Array.from({ length: Math.max(1, Math.min(5, r.rating || 5)) }).map((_, s) => (
                      <Star key={s} size={13} className="fill-brand-gold text-brand-gold" />
                    ))}
                  </div>
                  <p className="mt-3 text-sm italic text-brand-ink-muted line-clamp-3">"{r.text}"</p>
                  <p className="mt-4 font-serif text-base font-semibold text-brand-burgundy">{r.name}</p>
                  <p className="text-xs uppercase tracking-[0.15em] text-brand-gold">{r.service}</p>
                  <div className="mt-4 flex gap-2">
                    <button data-testid={`admin-review-edit-${r.id}`} onClick={() => setReviewForm({ ...r })} className="flex items-center gap-1.5 rounded-full border border-brand-gold/40 px-4 py-2 text-xs text-brand-burgundy transition-colors hover:bg-brand-burgundy hover:text-brand-cream">
                      <Pencil size={12} /> Edit
                    </button>
                    <button data-testid={`admin-review-delete-${r.id}`} onClick={() => deleteReview(r.id)} className="flex items-center gap-1.5 rounded-full border border-red-200 px-4 py-2 text-xs text-red-700 transition-colors hover:bg-red-600 hover:text-white">
                      <Trash2 size={12} /> Hapus
                    </button>
                  </div>
                </div>
              ))}
              {reviews.length === 0 && <p className="text-sm text-brand-ink-muted">Belum ada review. Tambahkan review pertama.</p>}
            </div>
          </div>
        )}

        {tab === "settings" && settings && (
          <div data-testid="admin-settings-panel" className="max-w-2xl">
            <h2 className="mb-6 font-serif text-2xl font-semibold text-brand-burgundy">Pengaturan Website</h2>
            <div className="space-y-5 rounded-sm bg-white gold-foil-border p-8">
              <div>
                <label className={labelCls}>Nomor WhatsApp Admin (format: 628xxx)</label>
                <input data-testid="settings-whatsapp-input" value={settings.whatsapp_number} onChange={(e) => setSettings({ ...settings, whatsapp_number: e.target.value })} className={inputCls} placeholder="6281234567890" />
                <p className="mt-1 text-xs text-brand-ink-muted">Semua tombol booking di website akan diarahkan ke nomor ini.</p>
              </div>
              <div>
                <label className={labelCls}>Link Instagram</label>
                <input data-testid="settings-instagram-input" value={settings.instagram_url} onChange={(e) => setSettings({ ...settings, instagram_url: e.target.value })} className={inputCls} placeholder="https://instagram.com/..." />
              </div>
              <div>
                <label className={labelCls}>Link TikTok</label>
                <input data-testid="settings-tiktok-input" value={settings.tiktok_url} onChange={(e) => setSettings({ ...settings, tiktok_url: e.target.value })} className={inputCls} placeholder="https://tiktok.com/@..." />
              </div>
              <div>
                <label className={labelCls}>Link Shopee</label>
                <input data-testid="settings-shopee-input" value={settings.shopee_url} onChange={(e) => setSettings({ ...settings, shopee_url: e.target.value })} className={inputCls} placeholder="https://shopee.co.id/..." />
              </div>
              <div>
                <label className={labelCls}>Alamat Salon</label>
                <input data-testid="settings-address-input" value={settings.address} onChange={(e) => setSettings({ ...settings, address: e.target.value })} className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>Jam Operasional</label>
                <input data-testid="settings-hours-input" value={settings.hours} onChange={(e) => setSettings({ ...settings, hours: e.target.value })} className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>URL Embed Google Maps</label>
                <input data-testid="settings-maps-input" value={settings.maps_embed_url} onChange={(e) => setSettings({ ...settings, maps_embed_url: e.target.value })} className={inputCls} placeholder="https://www.google.com/maps?q=...&output=embed" />
              </div>
              <ImageField label="Foto Hero Utama (opsional)" value={settings.hero_image || ""} onChange={(v) => setSettings({ ...settings, hero_image: v })} testid="settings-hero-image" />
              <button data-testid="settings-save-btn" onClick={saveSettings} className="inline-flex items-center gap-2 rounded-full bg-brand-burgundy px-8 py-3.5 text-xs font-bold uppercase tracking-[0.2em] text-brand-cream transition-colors hover:bg-brand-gold hover:text-brand-ink">
                Simpan Pengaturan
              </button>
            </div>
          </div>
        )}
      </div>

      <Dialog open={!!promoForm} onOpenChange={() => setPromoForm(null)}>
        <DialogContent className="max-w-lg bg-brand-cream gold-foil-border" data-testid="admin-promo-dialog">
          {promoForm && (
            <>
              <DialogHeader><DialogTitle className="font-serif text-brand-burgundy">{promoForm.id ? "Edit Promo" : "Tambah Promo"}</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <div><label className={labelCls}>Nama Promo</label><input data-testid="promo-form-name" value={promoForm.name} onChange={(e) => setPromoForm({ ...promoForm, name: e.target.value })} className={inputCls} /></div>
                <div className="grid grid-cols-2 gap-4">
                  <div><label className={labelCls}>Harga Normal</label><input data-testid="promo-form-normal-price" value={promoForm.normal_price} onChange={(e) => setPromoForm({ ...promoForm, normal_price: e.target.value })} className={inputCls} placeholder="Rp 1.500.000" /></div>
                  <div><label className={labelCls}>Harga Promo</label><input data-testid="promo-form-promo-price" value={promoForm.promo_price} onChange={(e) => setPromoForm({ ...promoForm, promo_price: e.target.value })} className={inputCls} placeholder="Rp 999.000" /></div>
                </div>
                <div><label className={labelCls}>Masa Berlaku (teks)</label><input data-testid="promo-form-valid" value={promoForm.valid_until} onChange={(e) => setPromoForm({ ...promoForm, valid_until: e.target.value })} className={inputCls} placeholder="Berlaku s/d 31 Juli 2026" /></div>
                <div>
                  <label className={labelCls}>Tanggal Berakhir (untuk hitung mundur otomatis)</label>
                  <input data-testid="promo-form-valid-date" type="date" value={promoForm.valid_until_date || ""} onChange={(e) => setPromoForm({ ...promoForm, valid_until_date: e.target.value })} className={inputCls} />
                  <p className="mt-1 text-xs text-brand-ink-muted">Jika diisi, kartu promo akan menampilkan countdown hari/jam/menit/detik secara live.</p>
                </div>
                <div><label className={labelCls}>Benefit (satu per baris)</label><textarea data-testid="promo-form-benefits" value={benefitsText} onChange={(e) => setBenefitsText(e.target.value)} rows={3} className={`${inputCls} resize-none`} /></div>
                <ImageField label="Foto Promo (opsional)" value={promoForm.image} onChange={(v) => setPromoForm({ ...promoForm, image: v })} testid="promo-form-image" />
                <div className="flex items-center gap-3">
                  <Switch data-testid="promo-form-active" checked={promoForm.active} onCheckedChange={(v) => setPromoForm({ ...promoForm, active: v })} />
                  <span className="text-sm text-brand-ink">Promo aktif / tampil di website</span>
                </div>
                <button data-testid="promo-form-save-btn" onClick={savePromo} className="w-full rounded-full bg-brand-burgundy py-3.5 text-xs font-bold uppercase tracking-[0.2em] text-brand-cream transition-colors hover:bg-brand-gold hover:text-brand-ink">
                  Simpan Promo
                </button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!reviewForm} onOpenChange={() => setReviewForm(null)}>
        <DialogContent className="max-w-lg bg-brand-cream gold-foil-border" data-testid="admin-review-dialog">
          {reviewForm && (
            <>
              <DialogHeader><DialogTitle className="font-serif text-brand-burgundy">{reviewForm.id ? "Edit Review" : "Tambah Review"}</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div><label className={labelCls}>Nama Customer</label><input data-testid="review-form-name" value={reviewForm.name} onChange={(e) => setReviewForm({ ...reviewForm, name: e.target.value })} className={inputCls} placeholder="Amanda Putri" /></div>
                  <div><label className={labelCls}>Layanan</label><input data-testid="review-form-service" value={reviewForm.service} onChange={(e) => setReviewForm({ ...reviewForm, service: e.target.value })} className={inputCls} placeholder="Balayage" /></div>
                </div>
                <div>
                  <label className={labelCls}>Rating</label>
                  <select data-testid="review-form-rating" value={reviewForm.rating} onChange={(e) => setReviewForm({ ...reviewForm, rating: e.target.value })} className={inputCls}>
                    {[5, 4, 3, 2, 1].map((n) => <option key={n} value={n}>{n} bintang</option>)}
                  </select>
                </div>
                <div><label className={labelCls}>Isi Review</label><textarea data-testid="review-form-text" value={reviewForm.text} onChange={(e) => setReviewForm({ ...reviewForm, text: e.target.value })} rows={4} className={`${inputCls} resize-none`} placeholder="Tulis testimoni customer..." /></div>
                <button data-testid="review-form-save-btn" onClick={saveReview} className="w-full rounded-full bg-brand-burgundy py-3.5 text-xs font-bold uppercase tracking-[0.2em] text-brand-cream transition-colors hover:bg-brand-gold hover:text-brand-ink">
                  Simpan Review
                </button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!galleryForm} onOpenChange={() => setGalleryForm(null)}>
        <DialogContent className="max-w-lg bg-brand-cream gold-foil-border" data-testid="admin-gallery-dialog">
          {galleryForm && (
            <>
              <DialogHeader><DialogTitle className="font-serif text-brand-burgundy">{galleryForm.id ? "Edit Foto" : "Tambah Foto"}</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className={labelCls}>Tipe Gallery</label>
                    <select data-testid="gallery-form-type" value={galleryForm.type} onChange={(e) => setGalleryForm({ ...galleryForm, type: e.target.value, category: e.target.value === "coloring" ? "BASIC COLOR" : "Hair" })} className={inputCls}>
                      <option value="coloring">Coloring Gallery</option>
                      <option value="general">Gallery Umum</option>
                    </select>
                  </div>
                  <div>
                    <label className={labelCls}>Kategori</label>
                    <select data-testid="gallery-form-category" value={galleryForm.category} onChange={(e) => setGalleryForm({ ...galleryForm, category: e.target.value })} className={inputCls}>
                      {categoryOptions.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                </div>
                <div><label className={labelCls}>Judul / Keterangan</label><input data-testid="gallery-form-title" value={galleryForm.title} onChange={(e) => setGalleryForm({ ...galleryForm, title: e.target.value })} className={inputCls} placeholder="Basic Color — Chocolate Brown" /></div>
                <div><label className={labelCls}>Deskripsi</label><textarea data-testid="gallery-form-description" value={galleryForm.description} onChange={(e) => setGalleryForm({ ...galleryForm, description: e.target.value })} rows={2} className={`${inputCls} resize-none`} placeholder="Hair coloring dengan hasil natural dan elegan." /></div>
                <ImageField label="Foto Utama" value={galleryForm.image} onChange={(v) => setGalleryForm({ ...galleryForm, image: v })} testid="gallery-form-image" />
                <button data-testid="gallery-form-save-btn" onClick={saveGallery} className="w-full rounded-full bg-brand-burgundy py-3.5 text-xs font-bold uppercase tracking-[0.2em] text-brand-cream transition-colors hover:bg-brand-gold hover:text-brand-ink">
                  Simpan Foto
                </button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
